import React from 'react';
import { Link2, Check, Download } from 'lucide-react';
import { formatEUR, priceLabel } from './lib';
import { AlternativePicker, RichText, TermsBlock, FeedbackBlock, QuestionBlock, AcceptanceBlock, FooterBlock, SectionsBlock, TotalsBlock, QuoteMetaBlock } from './SharedBlocks';

const SwissLayout = ({ ctx, theme }) => {
  const {
    quote, company, viewHash,
    einmaligServices, monatlichServices, jaehrlichServices,
    einmaligTotals, monatlichTotals, jaehrlichTotals,
    selectedAlternatives, selectAlternative, optionalServicesSelected, toggleOptionalService,
    lineTotal, handleSaveAsPdf, handleCopyLink, linkCopied, setShowAcceptanceForm,
  } = ctx;
  const cust = quote.customerData || {};
  const accent = theme.primary;
  const headBtn = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '7px 13px', borderRadius: 0, background: '#fff', border: `1px solid ${theme.textPrimary}`, color: theme.textPrimary, cursor: 'pointer', fontSize: 12, textTransform: 'uppercase', letterSpacing: .5 };

  const Row = ({ service, suffix, no }) => {
    const n = String(no).padStart(2, '0');
    const isOpt = service.isOptional;
    const on = !isOpt || optionalServicesSelected[service.id];
    return (
      <div style={{ padding: '16px 0', borderBottom: `1px solid ${theme.surfaceBorder}`, opacity: isOpt && !on ? 0.4 : 1 }}>
        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: accent, minWidth: 28, fontVariantNumeric: 'tabular-nums' }}>{n}</span>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
              {isOpt && <input type="checkbox" checked={!!optionalServicesSelected[service.id]} onChange={() => toggleOptionalService(service.id)} style={{ width: 16, height: 16, accentColor: accent, cursor: 'pointer' }} />}
              <p style={{ fontSize: 17, fontWeight: 700, color: theme.textPrimary, margin: 0, letterSpacing: '-0.3px', textTransform: 'uppercase' }}>{service.name}</p>
            </div>
            {service.description && <p style={{ fontSize: 13, color: theme.textSecondary, marginTop: 5, lineHeight: 1.5 }}>{service.description}</p>}
            <AlternativePicker service={service} theme={theme} selectedAlternatives={selectedAlternatives} onSelect={selectAlternative} />
          </div>
          <span style={{ fontSize: 17, fontWeight: 700, color: theme.textPrimary, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{priceLabel(service, lineTotal(service))}{suffix ? <span style={{ fontSize: 11, fontWeight: 400, color: theme.textMuted }}> {suffix}</span> : null}</span>
        </div>
      </div>
    );
  };
  const Group = ({ title, services, suffix, startNo }) => services.length ? (
    <div style={{ marginTop: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 2 }}>
        <span style={{ width: 24, height: 4, background: accent }} />
        <h3 style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: theme.textPrimary, margin: 0 }}>{title}</h3>
      </div>
      {services.map((s, i) => <Row key={s.id || i} service={s} suffix={suffix} no={startNo + i} />)}
    </div>
  ) : null;

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: theme.font, color: theme.textPrimary, padding: '0 0 64px' }}>
      <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 28px' }}>
        <div className="no-print" style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '16px 0' }}>
          <button onClick={handleSaveAsPdf} style={headBtn}><Download className="w-4 h-4" />PDF</button>
          <button onClick={handleCopyLink} style={headBtn}>{linkCopied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}{linkCopied ? 'Kopiert' : 'Link'}</button>
        </div>

        {/* Masthead */}
        <div style={{ borderTop: `3px solid ${theme.textPrimary}`, borderBottom: `1px solid ${theme.textPrimary}`, padding: '10px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase' }}>Angebot</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: accent }}>N° {String(quote.quoteNumber || '').replace(/\D/g, '').slice(-4) || '1024'}</span>
        </div>

        <h1 style={{ fontSize: 56, fontWeight: 800, lineHeight: 0.95, letterSpacing: '-2px', margin: '24px 0 0', color: theme.textPrimary, textTransform: 'uppercase' }}>
          {(cust.company || quote.heroHeadline || 'Ihr Angebot')}
        </h1>
        <div style={{ width: 60, height: 5, background: accent, margin: '16px 0' }} />
        {quote.quoteIntro && <RichText value={quote.quoteIntro} style={{ fontSize: 16, color: theme.textSecondary, lineHeight: 1.6, maxWidth: 560, marginTop: 12}} />}

        {quote.customerArguments && (
          <div style={{ borderTop: `1px solid ${theme.surfaceBorder}`, borderBottom: `1px solid ${theme.surfaceBorder}`, padding: '18px 0', margin: '20px 0', fontSize: 15, color: theme.textPrimary, lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: quote.customerArguments }} />
        )}
        {quote.videoUrl && <div className="no-print" style={{ margin: '18px 0' }}><video src={quote.videoUrl} controls style={{ width: '100%' }} /></div>}

        <QuoteMetaBlock quote={quote} theme={theme} company={company} />
        <Group title="Einmalige Leistungen" services={einmaligServices} suffix="" startNo={1} />
        <Group title="Monatliche Leistungen" services={monatlichServices} suffix="/ Monat" startNo={einmaligServices.length + 1} />
        <Group title="Jährliche Leistungen" services={jaehrlichServices} suffix="/ Jahr" startNo={einmaligServices.length + monatlichServices.length + 1} />

        <div style={{ borderTop: `3px solid ${theme.textPrimary}`, marginTop: 24, padding: '16px 0', display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>Gesamt netto</span>
          <span style={{ fontSize: 34, fontWeight: 800, letterSpacing: '-1px', fontVariantNumeric: 'tabular-nums' }}>{formatEUR(einmaligTotals.netto)}</span>
        </div>

        <button onClick={() => setShowAcceptanceForm(true)} className="no-print" style={{ width: '100%', padding: '15px', background: accent, color: '#fff', border: 'none', cursor: 'pointer', fontSize: 14, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 24 }}>
          Angebot annehmen →
        </button>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <TermsBlock quote={quote} company={company} theme={theme} />
          <TotalsBlock ctx={ctx} theme={theme} />
          <SectionsBlock quote={quote} theme={theme} ctx={ctx} />
          <AcceptanceBlock ctx={ctx} theme={theme} />
          <div className="no-print" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <FeedbackBlock viewHash={viewHash} theme={theme} />
            <QuestionBlock viewHash={viewHash} theme={theme} />
          </div>
          <FooterBlock company={company} theme={theme} />
        </div>
      </div>
    </div>
  );
};

export default SwissLayout;
